
import { useState, useEffect, useRef, useMemo } from 'react';
import { LibraryDB } from '../services/LibraryDB';
import { DownloadConfirmModal } from './DownloadConfirmModal';
import { downloadManager } from '../lib/DownloadManager';
import { GutendexService } from '../services/Gutendex';
import { PublicDomainCorpus } from '../services/PublicDomainCorpus';
import { getCoverUrl } from '../utils/coverHelper';
import { fetchTextViaWorker, fetchArrayBufferViaWorker } from '../utils/fetchProxy';
import { LocalImporter } from '../services/LocalImporter';

// Curated Shelf — ordered list of handpicked titles.
// Order here is the display order in the carousel. IDs must match manifest slugs.
const BOOK_CLUB_IDS = [
    'siddhartha-hermann-hesse',                                          // Siddhartha — Hermann Hesse
    'nexusism',                                                          // Nexusism
    'dubliners--james-joyce',                                            // Dubliners — James Joyce
    'war-and-peace--leo-tolstoy',                                        // War and Peace — Tolstoy
    'art-of-war--sun-tzu',                                               // The Art of War — Sun Tzu
    'origin-of-species--charles-darwin',                                 // On the Origin of Species — Darwin
    'incidents-in-the-life-of-a-slave-girl-written-by-herself-jacobs',  // Incidents in the Life of a Slave Girl — Jacobs
    'beyond-good-and-evil',                                              // Beyond Good and Evil — Nietzsche
    'crime-and-punishment--fyodor-dostoevsky',                           // Crime and Punishment — Dostoevsky
    'metamorphosis--franz-kafka',                                        // The Metamorphosis — Kafka
];

interface Props {
    onOpenBook: (id: string) => void;
    onPlayAudio?: (bookId: string, customAudio?: { url: string, title: string, coverUrl?: string }) => void;
    manifest: any[];
    onToast?: (msg: string) => void;
    [key: string]: any;
}

type ViewMode = 'landing' | 'browse_club' | 'browse_pd';

// Internal Carousel Component for Modern UX
const BookCarousel = ({ title, children, onViewAll }: { title: string, children: React.ReactNode, onViewAll?: () => void }) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const scroll = (dir: 'left' | 'right') => {
        if (scrollRef.current) {
            const amount = 300; // ~2 cards
            scrollRef.current.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
        }
    };

    return (
        <section>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h3 style={{ fontSize: '1rem', margin: 0, opacity: 0.8 }}>{title}</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {onViewAll && (
                        <button onClick={onViewAll} style={{
                            background: 'none', border: 'none', color: 'var(--accent-color)',
                            fontSize: '0.85rem', cursor: 'pointer'
                        }}>View All</button>
                    )}
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <button onClick={() => scroll('left')} style={{
                            width: '24px', height: '24px', borderRadius: '50%', border: '1px solid var(--border-color)',
                            background: 'var(--surface-color)', color: 'var(--text-color)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: 0.7
                        }}>‹</button>
                        <button onClick={() => scroll('right')} style={{
                            width: '24px', height: '24px', borderRadius: '50%', border: '1px solid var(--border-color)',
                            background: 'var(--surface-color)', color: 'var(--text-color)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: 0.7
                        }}>›</button>
                    </div>
                </div>
            </div>

            <div
                ref={scrollRef}
                className="hide-scrollbar"
                style={{
                    display: 'flex', gap: '15px', overflowX: 'auto', paddingBottom: '5px',
                    scrollSnapType: 'x mandatory',
                    margin: '0 -20px', padding: '0 20px' // Bleed effect
                }}
            >
                {children}
            </div>
        </section>
    );
};

const getGradientForTitle = (title: string) => {
    let hash = 0;
    for (let i = 0; i < title.length; i++) {
        hash = title.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
        ['#34495e', '#2c3e50'], // Midnight Blue
        ['#2980b9', '#2c3e50'], // Ocean Blue
        ['#c0392b', '#962d22'], // Crimson
        ['#27ae60', '#1e8449'], // Emerald
        ['#8e44ad', '#6c3483'], // Amethyst
        ['#d35400', '#a04000'], // Pumpkin Orange
        ['#16a085', '#117a65'], // Teal
        ['#7f8c8d', '#5d6d7e'], // Steel Gray
    ];
    const index = Math.abs(hash) % colors.length;
    return `linear-gradient(135deg, ${colors[index][0]} 0%, ${colors[index][1]} 100%)`;
};

const BookCoverPlaceholder = ({ title, author, style }: { title: string, author?: string, style?: any }) => {
    const gradient = getGradientForTitle(title);
    
    // Get initials or short author text
    let shortAuthor = 'Unknown Author';
    if (author) {
        const parts = author.split(',').map(s => s.trim());
        if (parts.length > 1) {
            // e.g. "Austen, Jane" -> "Jane Austen"
            shortAuthor = `${parts[1]} ${parts[0]}`;
        } else {
            shortAuthor = author;
        }
        if (shortAuthor.length > 20) {
            shortAuthor = shortAuthor.slice(0, 18) + '...';
        }
    }

    return (
        <div style={{
            ...style,
            background: gradient,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '16px 12px',
            boxSizing: 'border-box' as const,
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            textAlign: 'center',
            position: 'relative' as const,
            overflow: 'hidden'
        }}>
            {/* Elegant overlay pattern */}
            <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
                backgroundSize: '10px 10px',
                pointerEvents: 'none'
            }} />
            
            {/* Top Emblem */}
            <div style={{
                fontSize: '14px',
                opacity: 0.7,
                alignSelf: 'center'
            }}>
                📖
            </div>

            {/* Title */}
            <div style={{
                fontWeight: 700,
                fontSize: '12px',
                lineHeight: 1.3,
                maxHeight: '5.2em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                margin: 'auto 0'
            }}>
                {title}
            </div>

            {/* Author */}
            <div style={{
                fontSize: '10px',
                opacity: 0.85,
                fontWeight: 500,
                borderTop: '1px solid rgba(255,255,255,0.2)',
                paddingTop: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
            }}>
                {shortAuthor}
            </div>
        </div>
    );
};

// Internal Cover Image with Fallback Chain
const CoverImage = ({ src, bookId, alt, author, isPD, className, style }: { src?: string, bookId: string, alt: string, author?: string, isPD?: boolean, className?: string, style?: any }) => {
    const initialSrc = useMemo(() => {
        if (isPD) return src;
        return getCoverUrl(src, 'grid', bookId);
    }, [src, isPD, bookId]);
    const [imgSrc, setImgSrc] = useState(initialSrc);
    const [hasError, setHasError] = useState(false);

    // Reset if src/bookId changes
    useEffect(() => {
        setImgSrc(initialSrc);
        setHasError(false);
    }, [initialSrc]);

    const handleError = () => {
        if (isPD) {
            // For Gutendex results, if formats["image/jpeg"] is missing/broken, immediately render placeholder
            setHasError(true);
        } else {
            const currentSrc = imgSrc || '';
            if (currentSrc.includes('cover_grid.webp')) {
                setImgSrc(getCoverUrl(src, 'full', bookId));
            } else if (currentSrc.includes('cover_full.webp')) {
                setImgSrc(getCoverUrl(src, 'master', bookId));
            } else {
                setHasError(true);
            }
        }
    };

    if (hasError || !imgSrc) {
        return <BookCoverPlaceholder title={alt} author={author} style={style} />;
    }

    return (
        <img
            src={imgSrc}
            alt={alt}
            className={className}
            style={style}
            loading="lazy"
            onError={handleError}
        />
    );
};

interface SearchResultModel {
    id: string;
    title: string;
    author: string;
    source: 'claranarratio' | 'standard_ebooks' | 'librivox' | 'gutendex';
    sourceLabel: string;
    sourceRank: number;
    isLocal: boolean;
    isDownloaded: boolean;
    hasAudio: boolean;
    hasReaderText: boolean;
    coverUrl?: string;
    actionLabel: string;
    rawBook: any;
}

const normalizeAuthorName = (name: string): string => {
    if (!name) return 'Unknown';
    const parts = name.split(',').map(s => s.trim());
    if (parts.length > 1) {
        return `${parts[1]} ${parts[0]}`;
    }
    return name;
};

const searchStandardEbooks = async (query: string): Promise<any[]> => {
    try {
        const url = `https://standardebooks.org/ebooks?query=${encodeURIComponent(query)}`;
        const htmlText = await fetchTextViaWorker(url);
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');
        
        const bookElements = doc.querySelectorAll('li[typeof="schema:Book"]');
        const results: any[] = [];
        
        bookElements.forEach(li => {
            const about = li.getAttribute('about');
            if (!about) return;
            
            const parts = about.split('/').filter(Boolean);
            if (parts.length < 3) return;
            
            const authorSlug = parts[1];
            const bookSlug = parts[2];
            const id = `se-${authorSlug}-${bookSlug}`;
            
            const titleEl = li.querySelector('p a [property="schema:name"]');
            const title = titleEl?.textContent || bookSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            
            const authorEl = li.querySelector('p.author a [property="schema:name"]');
            const author = authorEl?.textContent || authorSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            
            const imgEl = li.querySelector('img');
            const imgSrc = imgEl?.getAttribute('src');
            const coverUrl = imgSrc ? (imgSrc.startsWith('http') ? imgSrc : `https://standardebooks.org${imgSrc}`) : undefined;
            
            const epubUrl = `https://standardebooks.org/ebooks/${authorSlug}/${bookSlug}/downloads/${authorSlug}_${bookSlug}.epub`;
            
            results.push({
                id,
                title,
                author,
                coverUrl,
                epubUrl,
                source: 'standard_ebooks',
                authorSlug,
                bookSlug
            });
        });
        
        return results;
    } catch (e) {
        console.warn("[StandardEbooks Search] Failed:", e);
        throw e;
    }
};

const searchLibriVox = async (query: string): Promise<any[]> => {
    try {
        const url = `https://librivox.org/api/feed/audiobooks/?title=${encodeURIComponent(query)}&format=json`;
        const jsonText = await fetchTextViaWorker(url);
        const data = JSON.parse(jsonText);
        
        let books: any[] = [];
        if (Array.isArray(data)) {
            books = data;
        } else if (data && Array.isArray(data.books)) {
            books = data.books;
        }
        
        return books.map((b: any) => {
            const id = `lv-${b.id}`;
            let authorName = 'Unknown Author';
            if (Array.isArray(b.authors) && b.authors.length > 0) {
                const a = b.authors[0];
                authorName = [a.first_name, a.last_name].filter(Boolean).join(' ');
            }
            
            return {
                id,
                title: b.title,
                author: authorName,
                description: b.description,
                rssUrl: b.url_rss,
                zipUrl: b.url_zip_file,
                librivoxUrl: b.url_librivox,
                totalTime: b.totaltime,
                source: 'librivox',
                rawId: b.id
            };
        });
    } catch (e) {
        console.warn("[LibriVox Search] Failed:", e);
        throw e;
    }
};

export const DiscoverView = ({ onOpenBook, onPlayAudio, manifest = [], onToast }: Props) => {
    // --- STATE ---
    const [mode, setMode] = useState<ViewMode>('landing');
    const [search, setSearch] = useState('');
    const [activeSearchQuery, setActiveSearchQuery] = useState('');

    // DATA STATE
    const [featured, setFeatured] = useState<SearchResultModel[]>([]); // Landing Top Carousel
    const [pdPopularHigh, setPdPopularHigh] = useState<SearchResultModel[]>([]); // Landing Row
    const [dbBooks, setDbBooks] = useState<any[]>([]); // Local DB books

    // SEARCH RESULTS
    const [localResults, setLocalResults] = useState<SearchResultModel[]>([]);
    const [remoteResults, setRemoteResults] = useState<SearchResultModel[]>([]);
    const [remoteLoading, setRemoteLoading] = useState(false);
    const [remoteError, setRemoteError] = useState<string | null>(null);

    const [standardResults, setStandardResults] = useState<SearchResultModel[]>([]);
    const [standardLoading, setStandardLoading] = useState(false);
    const [standardError, setStandardError] = useState<string | null>(null);

    const [librivoxResults, setLibrivoxResults] = useState<SearchResultModel[]>([]);
    const [librivoxLoading, setLibrivoxLoading] = useState(false);
    const [librivoxError, setLibrivoxError] = useState<string | null>(null);

    const [loadingAudioId, setLoadingAudioId] = useState<string | null>(null);

    const [searchElapsed, setSearchElapsed] = useState(0);
    const debounceTimeoutRef = useRef<any>(null);
    const searchCounterRef = useRef(0);

    // BROWSE PD STATE
    const [pdGrid, setPdGrid] = useState<SearchResultModel[]>([]);
    const [pdPage, setPdPage] = useState(1);
    const [pdLoading, setPdLoading] = useState(false);

    const [pdTopic, setPdTopic] = useState<string>('');
    const [pdQuery, setPdQuery] = useState<string>('');
    const [pdSort, setPdSort] = useState('popular');
    const [activeCategoryLabel, setActiveCategoryLabel] = useState('Popular');

    const [showMoreCats, setShowMoreCats] = useState(false);
    const [showDebug, setShowDebug] = useState(false);

    const [downloadingIds, setDownloadingIds] = useState<Record<string, boolean>>({});
    const [libraryItems, setLibraryItems] = useState<Record<string, boolean>>({});
    const [modalBook, setModalBook] = useState<any | null>(null);

    // --- P0: Explicit Book Club Definition ---
    const getBookClubStatus = (m: any) => {
        // 1. Explicit Flag from Worker (Primary Truth)
        if (m.isBookClub === true) return { isClub: true, reason: 'flag:isBookClub' };

        // 2. Tags (Secondary Truth)
        if (Array.isArray(m.tags) && m.tags.includes("CLUB")) return { isClub: true, reason: 'tag:CLUB' };

        // 3. Normalized Collection/Legacy (Fallback)
        // STRICT: Do NOT use playlistUrl as proxy.
        // Legacy Allowlist is ONLY for "Picks", not general membership, but we might want to respect it if it's there?
        // User said: "Allowlist is ONLY for Book Club Picks". So we exclude it here for "View All" membership checks.
        if (BOOK_CLUB_IDS.includes(m.id)) return { isClub: true, reason: 'id:BookClubList' };

        return { isClub: false, reason: 'none' };
    };

    const normalizeToSearchResult = (
        item: any,
        type: 'claranarratio' | 'standard_ebooks' | 'librivox' | 'gutendex',
        dbBook?: any
    ): SearchResultModel => {
        const inLib = !!dbBook || libraryItems[item.id] || libraryItems[String(item.id)];
        
        // Find DB status if available
        const status = dbBook?.status;
        const audioState = dbBook?.audioDownloadState;
        
        const isDownloaded = status === 'ready' || audioState === 'downloaded_verified';
        
        const title = item.title || '';
        let author = 'Unknown';
        
        if (type === 'claranarratio') {
            author = item.author || 'Unknown';
            const isClub = getBookClubStatus(item).isClub;
            
            // Action label
            let actionLabel = 'Listen Now';
            if (isDownloaded) {
                actionLabel = 'Listen';
            } else if (inLib) {
                actionLabel = 'Open';
            }

            return {
                id: item.id,
                title,
                author,
                source: 'claranarratio',
                sourceLabel: inLib ? 'In My Library' : 'ClaraNarratio',
                sourceRank: 1,
                isLocal: true,
                isDownloaded,
                hasAudio: true,
                hasReaderText: true,
                coverUrl: item.coverUrl || `/api/library/cover/${item.id}`,
                actionLabel,
                rawBook: item
            };
        } else if (type === 'gutendex') {
            if (item.authors && item.authors.length > 0) {
                author = normalizeAuthorName(item.authors[0].name);
            } else if (item.author) {
                author = normalizeAuthorName(item.author);
            }
            
            const actionLabel = inLib ? 'Read ✓' : 'Save Book';
            
            return {
                id: String(item.id),
                title,
                author,
                source: 'gutendex',
                sourceLabel: inLib ? 'In My Library' : 'Project Gutenberg',
                sourceRank: 4,
                isLocal: inLib,
                isDownloaded,
                hasAudio: audioState === 'downloaded_verified',
                hasReaderText: status === 'ready',
                coverUrl: item.formats ? item.formats['image/jpeg'] : (item.coverUrl || undefined),
                actionLabel,
                rawBook: item
            };
        } else if (type === 'standard_ebooks') {
            author = item.author || 'Unknown';
            const actionLabel = inLib ? (status === 'ready' ? 'Read' : 'Read ✓') : 'Read / Import';
            return {
                id: item.id,
                title,
                author,
                source: 'standard_ebooks',
                sourceLabel: inLib ? 'In My Library' : 'Standard Ebooks',
                sourceRank: 2,
                isLocal: inLib,
                isDownloaded,
                hasAudio: false,
                hasReaderText: true,
                coverUrl: item.coverUrl,
                actionLabel,
                rawBook: item
            };
        } else { // librivox
            author = item.author || 'Unknown';
            return {
                id: item.id,
                title,
                author,
                source: 'librivox',
                sourceLabel: inLib ? 'In My Library' : 'LibriVox Audio',
                sourceRank: 3,
                isLocal: inLib,
                isDownloaded: audioState === 'downloaded_verified',
                hasAudio: true,
                hasReaderText: false,
                coverUrl: item.coverUrl,
                actionLabel: 'Listen',
                rawBook: item
            };
        }
    };

    const sortSearchResults = (results: SearchResultModel[], query: string): SearchResultModel[] => {
        const q = query.toLowerCase().trim();
        return [...results].sort((a, b) => {
            const aTitle = a.title.toLowerCase().trim();
            const bTitle = b.title.toLowerCase().trim();
            
            // 1. Exact title match
            const aExact = aTitle === q;
            const bExact = bTitle === q;
            if (aExact && !bExact) return -1;
            if (!aExact && bExact) return 1;
            
            // 2. Starts with query
            const aStarts = aTitle.startsWith(q);
            const bStarts = bTitle.startsWith(q);
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;

            // 3. Downloaded/saved status
            if (a.isDownloaded && !b.isDownloaded) return -1;
            if (!a.isDownloaded && b.isDownloaded) return 1;
            if (a.isLocal && !b.isLocal) return -1;
            if (!a.isLocal && b.isLocal) return 1;

            // 4. Default to alphabetical title
            return aTitle.localeCompare(bTitle);
        });
    };


    // --- EFFECTS ---

    // Search Timer Effect
    useEffect(() => {
        let interval: any = null;
        if (remoteLoading || standardLoading || librivoxLoading) {
            setSearchElapsed(0);
            interval = setInterval(() => {
                setSearchElapsed(prev => prev + 1);
            }, 1000);
        } else {
            setSearchElapsed(0);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [remoteLoading, standardLoading, librivoxLoading]);

    // 1. Debounce Search Cleanup
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    // 2. Load Library Status
    useEffect(() => {
        LibraryDB.getAllBooks().then(books => {
            setDbBooks(books);
            const map: Record<string, boolean> = {};
            books.forEach(b => map[b.id] = true);
            setLibraryItems(map);
        });
    }, []);

    // 3. Hydrate Featured (Landing)
    useEffect(() => {
        if (mode !== 'landing') return;

        const loadLanding = async () => {
            // A. Prepare Sources
            const clubBooks = manifest.filter(m => getBookClubStatus(m).isClub);
            // Shuffle
            const clubSource = clubBooks.sort(() => 0.5 - Math.random());

            let pdSource: any[] = [];
            try {
                const top = await GutendexService.search('', 'en', 1, 'popular');
                pdSource = top.results;
            } catch (e) { }

            // B. Interleave
            const combined: any[] = [];
            const seenIds = new Set<string>();
            const seenTitles = new Set<string>();

            const isDuplicate = (id: string, title: string) => {
                if (seenIds.has(String(id))) return true;
                const norm = title.toLowerCase().replace(/[^\w\s]/g, '').trim();
                for (let t of seenTitles) {
                    if (t.includes(norm) || norm.includes(t)) return true;
                }
                return false;
            };

            const add = (item: any, type: 'club' | 'pd') => {
                const dbBook = dbBooks.find(b => b.id === String(item.id));
                const norm = type === 'club' 
                    ? normalizeToSearchResult(item, 'claranarratio', dbBook) 
                    : normalizeToSearchResult(item, 'gutendex', dbBook);
                const cleanTitle = norm.title.toLowerCase().replace(/[^\w\s]/g, '').trim();

                if (isDuplicate(norm.id, cleanTitle)) return false;

                combined.push(norm);
                seenIds.add(String(norm.id));
                seenTitles.add(cleanTitle);
                seenIds.add(String(item.id));
                return true;
            };

            let cIdx = 0;
            let pIdx = 0;
            const LIMIT = 16;

            while (combined.length < LIMIT && (cIdx < clubSource.length || pIdx < pdSource.length)) {
                if (cIdx < clubSource.length) add(clubSource[cIdx++], 'club');

                if (combined.length < LIMIT && pIdx < pdSource.length) {
                    let added = false;
                    while (!added && pIdx < pdSource.length) {
                        if (add(pdSource[pIdx++], 'pd')) added = true;
                    }
                }
            }

            setFeatured(combined);

            try {
                const top = await GutendexService.search('', 'en', 1, 'popular');
                setPdPopularHigh(top.results.map((m: any) => {
                    const dbBook = dbBooks.find(b => b.id === String(m.id));
                    return normalizeToSearchResult(m, 'gutendex', dbBook);
                }));
            } catch (e) { }
        };
        loadLanding();
    }, [mode, manifest, dbBooks]);

    // 4. Hydrate PD Grid
    useEffect(() => {
        if (mode !== 'browse_pd') return;

        const loadPdGrid = async () => {
            setPdLoading(true);
            try {
                const res = await GutendexService.search(
                    pdQuery,
                    'en',
                    pdPage,
                    pdSort,
                    pdTopic
                );

                const norm = res.results.map((m: any) => {
                    const dbBook = dbBooks.find(b => b.id === String(m.id));
                    return normalizeToSearchResult(m, 'gutendex', dbBook);
                });
                if (pdPage === 1) setPdGrid(norm);
                else setPdGrid(prev => [...prev, ...norm]);

            } catch (e) { console.warn("PD Load Error", e); }
            finally { setPdLoading(false); }
        };
        loadPdGrid();
    }, [mode, pdQuery, pdTopic, pdPage, pdSort, dbBooks]);


    const getNormalizedKey = (title: string, author?: string) => {
        const t = (title || '').toLowerCase().replace(/[^\w]/g, '').trim();
        const cleanAuthor = normalizeAuthorName(author || '');
        const a = cleanAuthor.toLowerCase().replace(/[^\w]/g, '').trim();
        return `${t}::${a}`;
    };

    const deduplicatedStandard = useMemo(() => {
        const seenKeys = new Set<string>();
        localResults.forEach(r => {
            seenKeys.add(getNormalizedKey(r.title, r.author));
        });
        
        return standardResults.filter(r => {
            const key = getNormalizedKey(r.title, r.author);
            if (seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
        });
    }, [localResults, standardResults]);

    const deduplicatedLibriVox = useMemo(() => {
        const seenKeys = new Set<string>();
        localResults.forEach(r => {
            seenKeys.add(getNormalizedKey(r.title, r.author));
        });
        deduplicatedStandard.forEach(r => {
            seenKeys.add(getNormalizedKey(r.title, r.author));
        });
        
        return librivoxResults.filter(r => {
            const key = getNormalizedKey(r.title, r.author);
            if (seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
        });
    }, [localResults, deduplicatedStandard, librivoxResults]);

    const deduplicatedRemote = useMemo(() => {
        const seenKeys = new Set<string>();
        localResults.forEach(r => {
            seenKeys.add(getNormalizedKey(r.title, r.author));
        });
        deduplicatedStandard.forEach(r => {
            seenKeys.add(getNormalizedKey(r.title, r.author));
        });
        deduplicatedLibriVox.forEach(r => {
            seenKeys.add(getNormalizedKey(r.title, r.author));
        });
        
        return remoteResults.filter(r => {
            const key = getNormalizedKey(r.title, r.author);
            if (seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
        });
    }, [localResults, deduplicatedStandard, deduplicatedLibriVox, remoteResults]);

    const triggerSearch = (query: string) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        
        const trimmed = query.trim();
        if (!trimmed) {
            setActiveSearchQuery('');
            setLocalResults([]);
            setRemoteResults([]);
            setRemoteError(null);
            setRemoteLoading(false);
            setStandardResults([]);
            setStandardLoading(false);
            setStandardError(null);
            setLibrivoxResults([]);
            setLibrivoxLoading(false);
            setLibrivoxError(null);
            searchCounterRef.current++;
            return;
        }
        
        setActiveSearchQuery(trimmed);
        
        // 1. Immediately match and display local results (Book Club / Narrated + DB matches)
        const localMatched = manifest.filter(m =>
            (m.title && m.title.toLowerCase().includes(trimmed.toLowerCase())) ||
            (m.author && m.author.toLowerCase().includes(trimmed.toLowerCase()))
        );
        
        const matchedIds = new Set(localMatched.map(m => m.id));
        const dbMatched = dbBooks.filter(dbB => {
            if (matchedIds.has(dbB.id)) return false;
            return (dbB.title && dbB.title.toLowerCase().includes(trimmed.toLowerCase())) ||
                   (dbB.authors && dbB.authors.some((a: any) => a.name && a.name.toLowerCase().includes(trimmed.toLowerCase())));
        });

        const normalizedLocal = [
            ...localMatched.map(item => {
                const dbBook = dbBooks.find(b => b.id === item.id);
                return normalizeToSearchResult(item, 'claranarratio', dbBook);
            }),
            ...dbMatched.map(item => {
                const isBookClub = item.source === 'book-club' || !!item.isBookClub;
                let sourceType: 'claranarratio' | 'standard_ebooks' | 'gutendex' = 'gutendex';
                if (item.source === 'standard_ebooks') sourceType = 'standard_ebooks';
                else if (isBookClub) sourceType = 'claranarratio';
                return normalizeToSearchResult(item, sourceType, item);
            })
        ];

        const sortedLocal = sortSearchResults(normalizedLocal, trimmed);
        setLocalResults(sortedLocal);
        
        // 2. Clear previous remote results, and trigger remote loading
        setRemoteResults([]);
        setRemoteError(null);
        setRemoteLoading(true);

        setStandardResults([]);
        setStandardError(null);
        setStandardLoading(true);

        setLibrivoxResults([]);
        setLibrivoxError(null);
        setLibrivoxLoading(true);
        
        // 3. Kick off remote search asynchronously with Stale response guard
        const queryId = ++searchCounterRef.current;
        
        searchStandardEbooks(trimmed)
            .then(results => {
                if (queryId !== searchCounterRef.current) return;
                const normalized = results.map(r => {
                    const dbBook = dbBooks.find(b => b.id === r.id);
                    return normalizeToSearchResult(r, 'standard_ebooks', dbBook);
                });
                setStandardResults(normalized);
                setStandardLoading(false);
            })
            .catch(e => {
                if (queryId !== searchCounterRef.current) return;
                console.error("Standard Ebooks search failed", e);
                setStandardError(e.message || "Failed to search Standard Ebooks.");
                setStandardLoading(false);
            });

        searchLibriVox(trimmed)
            .then(results => {
                if (queryId !== searchCounterRef.current) return;
                const normalized = results.map(r => {
                    const dbBook = dbBooks.find(b => b.id === r.id);
                    return normalizeToSearchResult(r, 'librivox', dbBook);
                });
                setLibrivoxResults(normalized);
                setLibrivoxLoading(false);
            })
            .catch(e => {
                if (queryId !== searchCounterRef.current) return;
                console.error("LibriVox search failed", e);
                setLibrivoxError(e.message || "Failed to search LibriVox.");
                setLibrivoxLoading(false);
            });

        GutendexService.search(trimmed, 'en')
            .then(remote => {
                if (queryId !== searchCounterRef.current) return;
                const remoteNorm = remote.results.map((m: any) => {
                    const dbBook = dbBooks.find(b => b.id === String(m.id));
                    return normalizeToSearchResult(m, 'gutendex', dbBook);
                });
                setRemoteResults(remoteNorm);
                setRemoteLoading(false);
            })
            .catch(e => {
                if (queryId !== searchCounterRef.current) return;
                console.error("Remote search failed", e);
                setRemoteError(e.message || "Failed to search Project Gutenberg.");
                setRemoteLoading(false);
            });
    };

    const handleSearchChange = (val: string) => {
        setSearch(val);
        
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        
        const trimmed = val.trim();
        if (!trimmed) {
            setActiveSearchQuery('');
            setLocalResults([]);
            setRemoteResults([]);
            setRemoteError(null);
            setRemoteLoading(false);
            setStandardResults([]);
            setStandardLoading(false);
            setStandardError(null);
            setLibrivoxResults([]);
            setLibrivoxLoading(false);
            setLibrivoxError(null);
            searchCounterRef.current++;
            return;
        }
        
        debounceTimeoutRef.current = setTimeout(() => {
            triggerSearch(trimmed);
        }, 300);
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        triggerSearch(search.trim());
    };


    // --- HELPERS ---

    const handleCategorySelect = (label: string, topic: string) => {
        setActiveCategoryLabel(label);
        setPdTopic(topic);
        setPdQuery('');
        setPdPage(1);
        setPdGrid([]);
        setShowMoreCats(false);
    };

    const handleBack = () => {
        if (search || activeSearchQuery) {
            setSearch('');
            setActiveSearchQuery('');
            setLocalResults([]);
            setRemoteResults([]);
            setRemoteLoading(false);
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        } else if (mode !== 'landing') {
            setMode('landing');
            setPdPage(1);
        }
    };

    const handlePlayLibriVox = async (book: SearchResultModel) => {
        const raw = book.rawBook;
        if (!raw || !raw.rssUrl) {
            if (onToast) onToast("Audio RSS source not available.");
            return;
        }

        setLoadingAudioId(book.id);
        if (onToast) onToast(`Loading track playlist...`);

        try {
            const rssText = await fetchTextViaWorker(raw.rssUrl);
            const parser = new DOMParser();
            const doc = parser.parseFromString(rssText, 'application/xml');
            
            const items = doc.querySelectorAll('item');
            const tracks = Array.from(items).map((item, idx) => {
                const title = item.querySelector('title')?.textContent || `Part ${idx + 1}`;
                const enclosure = item.querySelector('enclosure');
                const url = enclosure?.getAttribute('url') || '';
                const size = parseInt(enclosure?.getAttribute('length') || '0', 10);
                return {
                    index: idx,
                    title: title.replace(/^<!\[CDATA\[(.*)\]\]>$/i, '$1').trim(),
                    url,
                    size
                };
            }).filter(t => t.url);

            if (tracks.length === 0) {
                throw new Error("No audio tracks found in RSS feed.");
            }

            let coverUrl = book.coverUrl;
            if (!coverUrl) {
                const itunesImage = doc.getElementsByTagName('itunes:image')[0];
                if (itunesImage) {
                    coverUrl = itunesImage.getAttribute('href') || undefined;
                }
                if (!coverUrl) {
                    const image = doc.getElementsByTagName('image')[0];
                    if (image) {
                        coverUrl = image.getElementsByTagName('url')[0]?.textContent || undefined;
                    }
                }
            }

            const playlist = { ok: true, tracks };
            const jsonStr = JSON.stringify(playlist);
            const playlistDataUrl = `data:application/json;base64,${btoa(unescape(encodeURIComponent(jsonStr)))}`;

            if (onPlayAudio) {
                onPlayAudio(book.id, {
                    url: playlistDataUrl,
                    title: book.title,
                    coverUrl: coverUrl
                });
            }
        } catch (e: any) {
            console.error("Failed to load LibriVox RSS", e);
            if (onToast) onToast(`Failed to load audio: ${e.message}`);
        } finally {
            setLoadingAudioId(null);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleSave = async (book: any, download: boolean) => {
        if (downloadingIds[book.id]) return;

        setDownloadingIds(p => ({ ...p, [book.id]: true }));
        if (onToast) onToast(download ? `Downloading ${book.title}...` : `Saved ${book.title}`);

        try {
            if (book.source === 'standard_ebooks' || (book.rawBook && book.rawBook.source === 'standard_ebooks')) {
                const raw = book.rawBook || book;
                const epubUrl = raw.epubUrl;
                if (download) {
                    const buffer = await fetchArrayBufferViaWorker(epubUrl);
                    const result = await LocalImporter.processEpubData(new Uint8Array(buffer), raw.title);
                    await LibraryDB.addBook({
                        id: raw.id,
                        title: raw.title,
                        authors: [{ name: raw.author }],
                        languages: ['en'],
                        coverUrl: raw.coverUrl,
                        formats: { 'application/epub+zip': epubUrl },
                        addedAt: Date.now(),
                        status: 'ready',
                        textContent: result.fullText,
                        chunkedChapters: result.chapters,
                        preferredFormatUrl: epubUrl,
                        source: 'standard_ebooks'
                    });
                    await LibraryDB.saveBookAsset(raw.id, result.fullText);
                } else {
                    await LibraryDB.addBook({
                        id: raw.id,
                        title: raw.title,
                        authors: [{ name: raw.author }],
                        languages: ['en'],
                        coverUrl: raw.coverUrl,
                        formats: { 'application/epub+zip': epubUrl },
                        addedAt: Date.now(),
                        status: 'metadata-only',
                        preferredFormatUrl: epubUrl,
                        source: 'standard_ebooks'
                    });
                }
            } else if (book.isBookClub) {
                const entry = {
                    id: book.id,
                    title: book.title,
                    authors: book.authors,
                    languages: ['en'],
                    coverUrl: book.coverUrl,
                    formats: book.formats || {},
                    addedAt: Date.now(),
                    status: (download ? 'downloading' : 'metadata-only') as any,
                    isBookClub: true,
                    source: 'book-club'
                };
                await LibraryDB.addBook(entry);
                if (download) {
                    downloadManager.startDownload(book.id, `/api/library/playlist/${book.id}`);
                }
            } else {
                // Public Domain / Gutendex book
                if (download) {
                    const pdCorpus = new PublicDomainCorpus();
                    await pdCorpus.downloadBook(book);
                } else {
                    const entry = {
                        id: book.id.toString(),
                        title: book.title,
                        authors: book.authors,
                        languages: book.languages || ['en'],
                        coverUrl: book.formats['image/jpeg'],
                        formats: book.formats || {},
                        addedAt: Date.now(),
                        status: 'metadata-only' as const,
                        isBookClub: false,
                        source: 'gutendex',
                        gutenbergId: book.id.toString()
                    };
                    await LibraryDB.addBook(entry);
                }
            }
            setLibraryItems(p => ({ ...p, [book.id]: true }));
            LibraryDB.getAllBooks().then(setDbBooks);
            if (download && onToast) onToast(`Downloaded ${book.title}`);
        } catch (e: any) {
            console.error(e);
            if (onToast) onToast("Error: " + e.message);
        } finally {
            setDownloadingIds(p => ({ ...p, [book.id]: false }));
            setModalBook(null);
        }
    };

    // --- RENDERERS ---

    const renderBookCard = (book: SearchResultModel, isGrid = false) => {
        const inLib = book.isLocal;
        const isManifest = book.source === 'claranarratio';
        const authorName = book.author;
        
        const cardStyle = isGrid ? {
            width: '100%', display: 'flex', flexDirection: 'column', gap: '8px',
            cursor: 'pointer', height: '100%'
        } : {
            width: '140px', display: 'flex', flexDirection: 'column', gap: '8px',
            cursor: 'pointer', flexShrink: 0, scrollSnapAlign: 'start'
        };

        const coverWrapperStyle = isGrid ? {
            width: '100%', aspectRatio: '2/3', background: '#222',
            borderRadius: '8px', overflow: 'hidden', position: 'relative' as const,
            boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
        } : {
            width: '140px', height: '210px', background: '#222',
            borderRadius: '8px', overflow: 'hidden', position: 'relative' as const,
            boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
        };
        
        const badgeColor = 
            book.source === 'claranarratio' ? { bg: 'rgba(212, 175, 55, 0.15)', text: '#f1c40f', border: 'rgba(212, 175, 55, 0.3)' } :
            book.source === 'standard_ebooks' ? { bg: 'rgba(46, 204, 113, 0.15)', text: '#2ecc71', border: 'rgba(46, 204, 113, 0.3)' } :
            book.source === 'librivox' ? { bg: 'rgba(155, 89, 182, 0.15)', text: '#9b59b6', border: 'rgba(155, 89, 182, 0.3)' } :
            { bg: 'rgba(52, 152, 219, 0.15)', text: '#3498db', border: 'rgba(52, 152, 219, 0.3)' }; // gutendex

        return (
            <div key={book.id} style={cardStyle} onClick={() => {
                if (book.source === 'librivox') handlePlayLibriVox(book);
                else if (inLib || isManifest) onOpenBook(book.id);
                else setModalBook(book.rawBook);
            }}>
                <div style={coverWrapperStyle}>
                    <CoverImage 
                        src={book.coverUrl} 
                        bookId={book.id} 
                        alt={book.title} 
                        author={authorName}
                        isPD={!isManifest}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                    {isManifest && (
                        <div style={{ 
                            position: 'absolute', 
                            top: 4, 
                            right: 4, 
                            background: 'gold', 
                            color: 'black', 
                            fontSize: '9px', 
                            padding: '1px 3px', 
                            borderRadius: '2px', 
                            fontWeight: 'bold' 
                        }}>
                            CLUB
                        </div>
                    )}
                    {inLib && (
                        <div style={{ 
                            position: 'absolute', 
                            top: 4, 
                            left: 4, 
                            background: '#4CAF50', 
                            color: 'white', 
                            fontSize: '9px', 
                            padding: '1px 3px', 
                            borderRadius: '2px', 
                            fontWeight: 'bold' 
                        }}>
                            ✓ Saved
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: 1 }}>
                    <div style={{ 
                        fontWeight: 600, 
                        fontSize: '13px', 
                        lineHeight: 1.2, 
                        maxHeight: '2.4em', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        display: '-webkit-box', 
                        WebkitLineClamp: 2, 
                        WebkitBoxOrient: 'vertical',
                        color: 'var(--text-color)' 
                    }}>
                        {book.title}
                    </div>
                    <div style={{ 
                        fontSize: '12px', 
                        color: 'var(--text-secondary)', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis' 
                    }}>
                        {authorName}
                    </div>

                    {/* Curator Note — shown for curated shelf items only */}
                    {isManifest && book.rawBook && book.rawBook.curatorNote && (
                        <div style={{
                            fontSize: '10px',
                            fontStyle: 'italic',
                            color: 'var(--text-secondary)',
                            lineHeight: 1.35,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            opacity: 0.85,
                            marginTop: '3px',
                        }}>
                            {book.rawBook.curatorNote}
                        </div>
                    )}
                    
                    {/* Source Badge */}
                    <div style={{
                        alignSelf: 'flex-start',
                        fontSize: '9px',
                        fontWeight: 700,
                        padding: '1px 4px',
                        borderRadius: '3px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.2px',
                        background: badgeColor.bg,
                        color: badgeColor.text,
                        border: `1px solid ${badgeColor.border}`,
                        marginTop: '4px',
                        marginBottom: '4px'
                    }}>
                        {book.sourceLabel}
                    </div>

                    {/* Action Button */}
                    <button style={{
                        marginTop: 'auto',
                        width: '100%',
                        padding: '5px 8px',
                        borderRadius: '5px',
                        border: 'none',
                        background: inLib ? 'var(--surface-hover)' : 'var(--accent-color)',
                        color: inLib ? 'var(--text-color)' : '#fff',
                        fontSize: '10px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textAlign: 'center'
                    }} onClick={(e) => {
                        e.stopPropagation();
                        if (book.source === 'librivox') handlePlayLibriVox(book);
                        else if (inLib || isManifest) onOpenBook(book.id);
                        else setModalBook(book.rawBook);
                    }}>
                        {loadingAudioId === book.id ? 'Loading...' : book.actionLabel}
                    </button>
                </div>
            </div>
        );
    };

    const containerStyle = {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px',
        width: '100%',
        boxSizing: 'border-box' as const
    };

    // Derived States
    const showSearchResults = !!activeSearchQuery;

    // State for progressive eligibility check
    const [verifiedIds, setVerifiedIds] = useState<Set<string>>(new Set());
    const [scanningProgress, setScanningProgress] = useState({ checked: 0, total: 0 });

    // Curated Shelf Logic
    // 1. Picks = curated list in the exact order defined by BOOK_CLUB_IDS
    const curatedPicks = BOOK_CLUB_IDS
        .map(id => manifest.find((m: any) => m.id === id))
        .filter(Boolean);

    // PROGRESSIVE CHECKER: Verify reachability for items without explicit flags
    useEffect(() => {
        if (!manifest || manifest.length === 0) return;

        let active = true;
        const toCheck = manifest.filter(m => {
            // Skip checks if we already know it's good via flags
            if (m.audioUrl || m.hasAudio || (m.trackCount && m.trackCount > 0)) return false;
            // Also skip if we already verified it
            if (verifiedIds.has(m.id)) return false;
            if (BOOK_CLUB_IDS.includes(m.id)) return false;
            if (m.isBookClub) return false;
            if (m.tags && m.tags.includes("CLUB")) return false;
            // Otherwise, we need to check
            return true;
        });

        if (toCheck.length === 0) return;

        setScanningProgress({ checked: 0, total: toCheck.length });

        const checkBatch = async () => {
            // Process in chunks to avoid net-spam
            const CHUNK_SIZE = 5;
            for (let i = 0; i < toCheck.length; i += CHUNK_SIZE) {
                if (!active) break;
                const chunk = toCheck.slice(i, i + CHUNK_SIZE);

                await Promise.all(chunk.map(async (m) => {
                    try {
                        const res = await fetch(`/api/library/playlist/${m.id}`, { method: 'HEAD' });
                        if (res.ok) {
                            setVerifiedIds(prev => {
                                const next = new Set(prev);
                                next.add(m.id);
                                return next;
                            });
                        }
                    } catch (e) { /* ignore */ }
                }));

                if (active) setScanningProgress(prev => ({ ...prev, checked: Math.min(prev.total, i + CHUNK_SIZE) }));
            }
        };

        checkBatch();

        return () => { active = false; };
    }, [manifest.length]); // Run once when manifest loads (length changes usually mean load)

    // 2. Library (View All) = All Narrated Content
    // Priority: Strong Audio Signal > Playlist Reachable > Metadata
    let allNarrated = manifest.filter(m => {
        if (BOOK_CLUB_IDS.includes(m.id)) return true; // Always include picks

        // 1. Strong Audio Verification (Physical media existence flags)
        if (m.audioUrl || m.hasAudio || (m.trackCount && m.trackCount > 0)) return true;

        // 2. Verified Reachability (Progressive Check)
        if (verifiedIds.has(m.id)) return true;

        // 3. Metadata (We know where it *should* be)
        if (m.playlistUrl) return true;

        // 4. Club Membership (Explicit curation)
        if (m.isBookClub) return true;
        if (m.tags && m.tags.includes("CLUB")) return true;

        return false;
    });

    // Debug Stats
    const debugStats: any = {};
    if (showDebug) {
        debugStats.totalManifest = manifest.length;
        debugStats.picksCount = curatedPicks.length;
        debugStats.libraryCount = allNarrated.length;
        debugStats.scanning = `${scanningProgress.checked}/${scanningProgress.total}`;
        debugStats.verifiedExtra = verifiedIds.size;
        debugStats.examplePick = curatedPicks[0]?.title;
        debugStats.exampleLib = allNarrated.find(m => !BOOK_CLUB_IDS.includes(m.id))?.title || "None (All are picks)";
    }

    return (
        <div style={{ background: 'var(--bg-color)', minHeight: '100%', color: 'var(--text-color)', paddingBottom: '80px', overflowY: 'auto' }}>

            {/* Header */}
            <div style={{ position: 'sticky', top: 0, zIndex: 50, background: 'var(--surface-color)', borderBottom: '1px solid var(--border-color)' }}>
                <div style={{ ...containerStyle, padding: '15px 20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {(mode !== 'landing' || showSearchResults) && (
                        <button onClick={handleBack} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: 5, color: 'var(--text-color)' }}>
                            ⬅
                        </button>
                    )}
                    <form onSubmit={handleSearchSubmit} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                            <input
                                value={search}
                                placeholder={mode === 'browse_club' ? "Search Book Club..." : mode === 'browse_pd' ? "Search Public Domain..." : "Search Library..."}
                                onChange={e => handleSearchChange(e.target.value)}
                                style={{
                                    width: '100%', padding: '12px 15px', borderRadius: '25px',
                                    border: '1px solid var(--border-color)',
                                    background: 'var(--bg-color)', color: 'var(--text-color)',
                                    fontSize: '1rem'
                                }}
                            />
                            {(remoteLoading || standardLoading || librivoxLoading) && <span style={{ position: 'absolute', right: 15, top: 12 }} className="spinner-mini" />}
                        </div>
                    </form>
                </div>
            </div>

            {/* CONTENT */}
            <div style={containerStyle}>

                {/* 1. SEARCH RESULTS */}
                {showSearchResults ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                                <h2 style={{ fontSize: '1.2rem', margin: 0 }}>From ClaraNarratio</h2>
                                <span style={{ 
                                    background: 'rgba(212, 175, 55, 0.15)', 
                                    color: '#f1c40f', 
                                    fontSize: '11px', 
                                    padding: '2px 8px', 
                                    borderRadius: '12px',
                                    fontWeight: 'bold'
                                }}>
                                    {localResults.length} {localResults.length === 1 ? 'match' : 'matches'}
                                </span>
                            </div>
                            {localResults.length === 0 ? (
                                <div style={{ opacity: 0.6, fontSize: '0.9rem', fontStyle: 'italic', padding: '10px 0' }}>
                                    No narrated library items match "{activeSearchQuery}".
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(135px, 1fr))', gap: '20px' }}>
                                    {localResults.map(b => renderBookCard(b, true))}
                                </div>
                            )}
                        </div>

                        <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '10px 0' }} />

                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                                <h2 style={{ fontSize: '1.2rem', margin: 0 }}>From Standard Ebooks</h2>
                                {standardLoading ? (
                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <span className="spinner-mini" />
                                        Searching Standard Ebooks ({searchElapsed}s)...
                                    </span>
                                ) : (
                                    <span style={{ 
                                        background: 'rgba(46, 204, 113, 0.15)', 
                                        color: '#2ecc71', 
                                        fontSize: '11px', 
                                        padding: '2px 8px', 
                                        borderRadius: '12px',
                                        fontWeight: 'bold'
                                    }}>
                                        {deduplicatedStandard.length} {deduplicatedStandard.length === 1 ? 'result' : 'results'}
                                    </span>
                                )}
                            </div>
                            {standardError && (
                                <div style={{ 
                                    color: 'var(--text-secondary)', 
                                    background: 'var(--surface-hover)', 
                                    border: '1px solid var(--border-color)', 
                                    padding: '10px 15px', 
                                    borderRadius: '8px', 
                                    fontSize: '0.85rem',
                                    marginBottom: '15px'
                                }}>
                                    ℹ️ Standard Ebooks catalog search is temporarily offline.
                                </div>
                            )}
                            {deduplicatedStandard.length === 0 && !standardLoading && !standardError ? (
                                <div style={{ opacity: 0.6, fontSize: '0.9rem', fontStyle: 'italic', padding: '10px 0' }}>
                                    No curated ebooks found matching "{activeSearchQuery}".
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(135px, 1fr))', gap: '20px' }}>
                                    {deduplicatedStandard.map(b => renderBookCard(b, true))}
                                </div>
                            )}
                        </div>

                        <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '10px 0' }} />

                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                                <h2 style={{ fontSize: '1.2rem', margin: 0 }}>From LibriVox Audio</h2>
                                {librivoxLoading ? (
                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <span className="spinner-mini" />
                                        Searching LibriVox ({searchElapsed}s)...
                                    </span>
                                ) : (
                                    <span style={{ 
                                        background: 'rgba(155, 89, 182, 0.15)', 
                                        color: '#9b59b6', 
                                        fontSize: '11px', 
                                        padding: '2px 8px', 
                                        borderRadius: '12px',
                                        fontWeight: 'bold'
                                    }}>
                                        {deduplicatedLibriVox.length} {deduplicatedLibriVox.length === 1 ? 'result' : 'results'}
                                    </span>
                                )}
                            </div>
                            {librivoxError && (
                                <div style={{ 
                                    color: 'var(--text-secondary)', 
                                    background: 'var(--surface-hover)', 
                                    border: '1px solid var(--border-color)', 
                                    padding: '10px 15px', 
                                    borderRadius: '8px', 
                                    fontSize: '0.85rem',
                                    marginBottom: '15px'
                                }}>
                                    ℹ️ LibriVox audiobook search is temporarily offline.
                                </div>
                            )}
                            {deduplicatedLibriVox.length === 0 && !librivoxLoading && !librivoxError ? (
                                <div style={{ opacity: 0.6, fontSize: '0.9rem', fontStyle: 'italic', padding: '10px 0' }}>
                                    No public domain audiobooks found matching "{activeSearchQuery}".
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(135px, 1fr))', gap: '20px' }}>
                                    {deduplicatedLibriVox.map(b => renderBookCard(b, true))}
                                </div>
                            )}
                        </div>

                        <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '10px 0' }} />

                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                                <h2 style={{ fontSize: '1.2rem', margin: 0 }}>From Project Gutenberg</h2>
                                {remoteLoading ? (
                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <span className="spinner-mini" />
                                        Searching Project Gutenberg ({searchElapsed}s)...
                                        {searchElapsed > 4 && (
                                            <span style={{ color: 'var(--warning-color, #fbbf24)', marginLeft: '10px', fontWeight: 'bold' }}>
                                                Still searching public-domain catalog...
                                            </span>
                                        )}
                                    </span>
                                ) : (
                                    <span style={{ 
                                        background: 'rgba(52, 152, 219, 0.15)', 
                                        color: '#3498db', 
                                        fontSize: '11px', 
                                        padding: '2px 8px', 
                                        borderRadius: '12px',
                                        fontWeight: 'bold'
                                    }}>
                                        {deduplicatedRemote.length} {deduplicatedRemote.length === 1 ? 'result' : 'results'}
                                    </span>
                                )}
                            </div>
                            {remoteError && (
                                <div style={{ 
                                    color: 'var(--text-secondary)', 
                                    background: 'var(--surface-hover)', 
                                    border: '1px solid var(--border-color)', 
                                    padding: '10px 15px', 
                                    borderRadius: '8px', 
                                    fontSize: '0.85rem',
                                    marginBottom: '15px'
                                }}>
                                    ℹ️ External catalog search is temporarily offline, but your local library matches are shown above.
                                </div>
                            )}
                            {deduplicatedRemote.length === 0 && !remoteLoading && !remoteError ? (
                                <div style={{ opacity: 0.6, fontSize: '0.9rem', fontStyle: 'italic', padding: '10px 0' }}>
                                    No public domain books found matching "{activeSearchQuery}".
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(135px, 1fr))', gap: '20px' }}>
                                    {deduplicatedRemote.map(b => renderBookCard(b, true))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        {/* 2. LANDING */}
                        {mode === 'landing' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>

                                <BookCarousel title="✨ Featured">
                                    {featured.length === 0 ? <div style={{ height: 210, width: 140, background: 'rgba(255,255,255,0.05)', borderRadius: 8 }} /> : (
                                        featured.map(b => <div key={b.id}>{renderBookCard(b)}</div>)
                                    )}
                                </BookCarousel>

                                <section style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div
                                        onClick={() => setMode('browse_club')}
                                        style={{
                                            background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                                            borderRadius: '12px', padding: '15px', color: 'white', cursor: 'pointer', height: '100px',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', justifyContent: 'center'
                                        }}
                                    >
                                        <div style={{ fontSize: '1.2rem' }}>🎧</div>
                                        <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>Book Club</div>
                                        <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Narrated Collection</div>
                                    </div>
                                    <div
                                        onClick={() => setMode('browse_pd')}
                                        style={{
                                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                            borderRadius: '12px', padding: '15px', color: 'white', cursor: 'pointer', height: '100px',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', justifyContent: 'center'
                                        }}
                                    >
                                        <div style={{ fontSize: '1.2rem' }}>🏛️</div>
                                        <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>Public Domain</div>
                                        <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Free E-Books</div>
                                    </div>
                                </section>

                                <BookCarousel title="✨ Curated Shelf" onViewAll={() => setMode('browse_club')}>
                                    {curatedPicks.length > 0 ? (
                                        curatedPicks.map((m: any) => {
                                            const dbBook = dbBooks.find(b => b.id === m.id);
                                            const normalized = normalizeToSearchResult(m, 'claranarratio', dbBook);
                                            return <div key={m.id}>{renderBookCard(normalized)}</div>;
                                        })
                                    ) : (
                                        allNarrated.slice(0, 10).map((m: any) => {
                                            const dbBook = dbBooks.find(b => b.id === m.id);
                                            const normalized = normalizeToSearchResult(m, 'claranarratio', dbBook);
                                            return <div key={m.id}>{renderBookCard(normalized)}</div>;
                                        })
                                    )}
                                    {curatedPicks.length === 0 && allNarrated.length === 0 && <div style={{ opacity: 0.5, padding: '20px', fontSize: '0.9rem' }}>Checking library...</div>}
                                </BookCarousel>

                                <BookCarousel title="Public Domain Top Downloads" onViewAll={() => setMode('browse_pd')}>
                                    {pdPopularHigh.map(b => (
                                        <div key={b.id}>{renderBookCard(b)}</div>
                                    ))}
                                </BookCarousel>
                            </div>
                        )}

                        {/* 3. BROWSE CLUB (View All) */}
                        {mode === 'browse_club' && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <h2 style={{ fontSize: '1.5rem', margin: 0 }}>✨ Curated Shelf</h2>
                                    <button onClick={() => setShowDebug(!showDebug)} style={{ fontSize: '0.75rem', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                                        {showDebug ? 'Hide Debug' : 'Debug'}
                                    </button>
                                </div>
                                <p style={{ opacity: 0.7, marginBottom: '20px', fontSize: '0.9rem' }}>Handpicked philosophy, literature, history — and one title that started here.</p>

                                {showDebug && (
                                    <div style={{ backgroundColor: '#222', color: '#0f0', padding: '10px', fontSize: '0.75rem', fontFamily: 'monospace', marginBottom: '20px', borderRadius: '6px' }}>
                                        <strong>Debug Stats:</strong>
                                        <div style={{ marginLeft: '10px' }}>
                                            Showing {allNarrated.length} of {allNarrated.length} eligible<br />
                                            Total Manifest: {manifest.length}<br />
                                            Scanning: {scanningProgress.checked}/{scanningProgress.total} (Found: {verifiedIds.size})<br />
                                            Picks Count: {curatedPicks.length}<br />
                                            Library Count: {allNarrated.length}
                                        </div>
                                    </div>
                                )}

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(135px, 1fr))', gap: '20px' }}>
                                    {allNarrated.map(m => {
                                        const dbBook = dbBooks.find(b => b.id === m.id);
                                        return normalizeToSearchResult(m, 'claranarratio', dbBook);
                                    }).map(b => renderBookCard(b, true))}
                                </div>
                                {allNarrated.length === 0 && <div style={{ padding: '20px' }}>No narrated books found in manifest.</div>}
                            </div>
                        )}

                        {/* 4. BROWSE PD */}
                        {mode === 'browse_pd' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Public Domain</h2>
                                    <button onClick={() => setShowMoreCats(true)} style={{ fontSize: '0.9rem', color: 'var(--accent-color)', background: 'none', border: 'none', cursor: 'pointer' }}>Categories...</button>
                                </div>

                                {/* Categories Strip */}
                                <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '5px' }} className="hide-scrollbar">
                                    {[
                                        { topic: '', label: 'Popular' },
                                        { topic: 'science fiction', label: 'Sci-Fi' },
                                        { topic: 'mystery', label: 'Mystery' },
                                        { topic: 'history', label: 'History' },
                                        { topic: 'biography', label: 'Biography' },
                                        { topic: 'horror', label: 'Horror' }
                                    ].map(cat => (
                                        <button
                                            key={cat.label}
                                            onClick={() => handleCategorySelect(cat.label, cat.topic)}
                                            style={{
                                                padding: '8px 16px', borderRadius: '20px', border: 'none',
                                                background: activeCategoryLabel === cat.label ? 'var(--accent-color)' : 'var(--surface-hover)',
                                                color: activeCategoryLabel === cat.label ? '#fff' : 'var(--text-color)',
                                                cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.9rem'
                                            }}
                                        >
                                            {cat.label}
                                        </button>
                                    ))}
                                </div>

                                {activeCategoryLabel !== 'Popular' && <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>Browsing: {activeCategoryLabel}</div>}

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(135px, 1fr))', gap: '20px' }}>
                                    {pdGrid.map(b => renderBookCard(b, true))}
                                </div>

                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    {pdLoading ? (
                                        <div>Loading...</div>
                                    ) : (
                                        <button onClick={() => setPdPage(p => p + 1)} style={{ padding: '10px 30px', borderRadius: '20px', border: '1px solid var(--border-color)', background: 'var(--surface-hover)', color: 'var(--text-color)', cursor: 'pointer' }}>
                                            Load More
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* MODALS */}
            {showMoreCats && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: 'var(--surface-color)', padding: '20px', borderRadius: '12px', width: '90%', maxWidth: '400px', maxHeight: '80vh', overflowY: 'auto' }}>
                        <h3 style={{ marginTop: 0 }}>All Categories</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            {[
                                "Adventure", "Children", "Comedy", "Drama", "Fantasy", "Fiction", "Folklore", "History", "Horror", "Humor", "Mystery", "Mythology", "Periodical", "Philosophy", "Poetry", "Politics", "Religion", "Romance", "Science", "Science Fiction", "Short Stories", "Thriller", "Travel"
                            ].map(c => (
                                <button key={c} onClick={() => handleCategorySelect(c, c.toLowerCase())} style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-color)', color: 'var(--text-color)', cursor: 'pointer', textAlign: 'left' }}>
                                    {c}
                                </button>
                            ))}
                        </div>
                        <button onClick={() => setShowMoreCats(false)} style={{ marginTop: '20px', width: '100%', padding: '12px', background: 'var(--surface-hover)', border: 'none', color: 'var(--text-color)', borderRadius: '6px', cursor: 'pointer' }}>Close</button>
                    </div>
                </div>
            )}

            {modalBook && (
                <DownloadConfirmModal
                    title={modalBook.title}
                    onClose={() => setModalBook(null)}
                    onSaveOnly={() => handleSave(modalBook, false)}
                    onSaveAndDownload={() => handleSave(modalBook, true)}
                />
            )}
        </div>
    );
};
